import { X, Award, ThumbsUp, CheckCircle, MessageCircle, User, MessageSquare, FileText, Star, MapPin, Camera, Heart, UserPlus, Hand, Coffee, Flag, Building2, Calendar, Flame, Users, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { Notification } from "@/hooks/useNotifications";

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  /** Shows a clear button that removes this one notification. */
  onClear?: () => void;
  /** Owner area uses larger text. */
  size?: "default" | "large";
  /** Overrides the icon shown for a type. */
  icon?: React.ElementType;
  iconClassName?: string;
}

const typeConfig: Record<string, { icon: React.ElementType; colorClass: string }> = {
  badge_earned: { icon: Award, colorClass: "text-honey bg-honey/10" },
  review_liked: { icon: ThumbsUp, colorClass: "text-sage bg-sage/10" },
  photo_liked: { icon: Heart, colorClass: "text-honey bg-honey/10" },
  submission_approved: { icon: CheckCircle, colorClass: "text-sage bg-sage/10" },
  feedback_resolved: { icon: MessageCircle, colorClass: "text-primary bg-primary/10" },
  follow: { icon: UserPlus, colorClass: "text-primary bg-primary/10" },
  wave: { icon: Hand, colorClass: "text-honey bg-honey/10" },
  friend_request: { icon: UserPlus, colorClass: "text-primary bg-primary/10" },
  friend_accepted: { icon: UserPlus, colorClass: "text-sage bg-sage/10" },
  new_cafe_nearby: { icon: Coffee, colorClass: "text-sage bg-sage/10" },
  grounded_post_liked: { icon: Heart, colorClass: "text-honey bg-honey/10" },
  check_in_liked: { icon: Heart, colorClass: "text-honey bg-honey/10" },
  ownership_claim_approved: { icon: CheckCircle, colorClass: "text-sage bg-sage/10" },
  ownership_claim_denied: { icon: XCircle, colorClass: "text-muted-foreground bg-muted" },
  admin_signup: { icon: User, colorClass: "text-primary bg-primary/10" },
  admin_feedback: { icon: MessageSquare, colorClass: "text-honey bg-honey/10" },
  admin_submission: { icon: FileText, colorClass: "text-sage bg-sage/10" },
  admin_review: { icon: Star, colorClass: "text-honey bg-honey/10" },
  admin_checkin: { icon: MapPin, colorClass: "text-sage bg-sage/10" },
  admin_photo: { icon: Camera, colorClass: "text-primary bg-primary/10" },
  admin_owner_description: { icon: FileText, colorClass: "text-sage bg-sage/10" },
  admin_owner_message: { icon: MessageSquare, colorClass: "text-honey bg-honey/10" },
  owner_message: { icon: MessageSquare, colorClass: "text-honey bg-honey/10" },
  owner_content_removed: { icon: Building2, colorClass: "text-muted-foreground bg-muted" },
  content_report: { icon: Flag, colorClass: "text-destructive bg-destructive/10" },
  ownership_claim: { icon: Building2, colorClass: "text-primary bg-primary/10" },
  weekly_recap: { icon: Calendar, colorClass: "text-primary bg-primary/10" },
  streak_milestone: { icon: Flame, colorClass: "text-honey bg-honey/10" },
  friends_activity_nudge: { icon: Users, colorClass: "text-sage bg-sage/10" },
};

// Notification types that have a person behind them — show their avatar
const SOCIAL_TYPES = new Set(["follow", "wave", "friend_request", "friend_accepted", "check_in_liked", "review_liked", "photo_liked", "grounded_post_liked"]);

function getActorId(notification: Notification): string | null {
  const data = notification.data as Record<string, string>;
  return data?.from_user_id ?? data?.follower_id ?? data?.liker_id ?? data?.liker_user_id ?? null;
}

function SocialAvatar({ userId, colorClass }: { userId: string; colorClass: string }) {
  const { data: profile } = useQuery({
    queryKey: ["notif-actor", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("public_profiles")
        .select("display_name, username, avatar_url")
        .eq("id", userId)
        .maybeSingle();
      return data;
    },
    staleTime: 5 * 60_000,
  });

  const name = profile?.display_name || profile?.username || "?";
  const initials = name.charAt(0).toUpperCase();

  return (
    <Avatar className="h-9 w-9 shrink-0">
      <AvatarImage src={profile?.avatar_url ?? undefined} alt={name} />
      <AvatarFallback className={cn("text-xs font-semibold", colorClass)}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export function NotificationItem({ notification, onClick, onClear, size = "default", icon, iconClassName }: NotificationItemProps) {
  const config = typeConfig[notification.type] ?? { icon: CheckCircle, colorClass: "text-muted-foreground bg-muted" };
  const Icon = icon ?? config.icon;
  const large = size === "large";
  const actorId = SOCIAL_TYPES.has(notification.type) ? getActorId(notification) : null;

  return (
    <div className="relative">
    <button
      onClick={onClick}
      className={cn(
        onClear && "pr-11",
        large && "py-2.5",
        "w-full flex items-start gap-3 p-3 text-left transition-colors hover:bg-secondary/50 rounded-xl relative",
        !notification.is_read && "bg-primary/5"
      )}
    >
      {!notification.is_read && (
        <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-primary" />
      )}
      {actorId ? (
        <SocialAvatar userId={actorId} colorClass={config.colorClass} />
      ) : (
        <div className={cn("h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0", iconClassName ?? config.colorClass)}>
          <Icon className="h-4 w-4" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className={cn(
          large ? "text-[16px] leading-snug" : "text-sm leading-snug",
          !notification.is_read ? "font-medium text-foreground" : "text-muted-foreground"
        )}>
          {notification.title}
        </p>
        {notification.message && (
          <p className={cn(large ? "text-[15px]" : "text-xs line-clamp-2", "text-muted-foreground mt-0.5")}>
            {notification.message}
          </p>
        )}
        <p className={cn(large ? "text-[15px] text-muted-foreground" : "text-[10px] text-muted-foreground/60", large ? "mt-0" : "mt-1")}>
          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
        </p>
      </div>

    </button>
    {onClear && (
      <button
        type="button"
        aria-label="Clear notification"
        onClick={(e) => { e.stopPropagation(); onClear(); }}
        className="absolute right-1.5 top-1.5 h-9 w-9 flex items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    )}
    </div>
  );
}
